import { useEffect, useMemo, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import { Card, Input, PaginadorTabla, Stack } from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { Area } from "@/lib/types";
import { useAreasAdminPage } from "../hooks/use-areas-admin";
import { listarAreasAdmin } from "../services/areas-service";
import { AreasHeader } from "./AreasHeader";
import { AreaCrearForm } from "./AreaCrearForm";
import { AreasTable } from "./AreasTable";

const PAGE_SIZE = 15;

/**
 * Página de administración de áreas · búsqueda y paginado se resuelven en
 * el SERVIDOR (query params con debounce de 350ms). El loader del route
 * precarga la primera página así que el primer paint es síncrono.
 */
export function AdminAreasPage() {
  const [modoCrear, setModoCrear] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const filtrosServer = useMemo(
    () => (busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
    [busquedaDebounced]
  );

  // Cambiar la búsqueda invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [busquedaDebounced]);

  const { data } = useAreasAdminPage({ ...filtrosServer, page, pageSize: PAGE_SIZE });
  const areas = data?.items ?? [];
  const total = data?.total ?? 0;

  // Export a Excel · trae TODAS las páginas con la búsqueda actual.
  const obtenerTodas = async (): Promise<Area[]> => {
    const todas: Area[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarAreasAdmin({ ...filtrosServer, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AreasHeader total={data?.totalCatalogo} modoCrear={modoCrear} onIniciarCrear={() => setModoCrear(true)} />
      <Stack gap="md">
        {modoCrear ? <AreaCrearForm onSalir={() => setModoCrear(false)} /> : null}
        <Card padding="lg">
          <Stack gap="md">
            <Input
              label="Buscar"
              placeholder="nombre o descripción…"
              leftSection={<IconSearch size={14} stroke={1.7} />}
              value={busqueda}
              onChange={(e) => setBusqueda(e.currentTarget.value)}
              wrapperClassName="max-w-90"
            />
            <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
            <AreasTable cargando={!data} areas={areas} obtenerFilas={obtenerTodas} />
            <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="áreas" />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
}
